/**
 * Deliberate duplicate of `apps/mobile/src/pools/joinPoolFlow.ts`.
 *
 * Normalizes what the fan types into exactly the string the server accepts,
 * and decides from the join preview whether a Join button may be offered at
 * all. Mirrors `services/pool-api/src/lib/joinCode.ts`.
 *
 * There is no shared non-SDK business-logic package (`mobile-core` is
 * React-Native-only), so these pure functions are duplicated here rather than
 * imported across apps. The two files must change together; do not let them
 * drift.
 */

export const JOIN_CODE_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
export const JOIN_CODE_LENGTH = 8;

// A shared link names its code in the path (`/join/ABCD1234`, also the app's
// `sportsmet://join/ABCD1234`) or in a query param (`/pools?joinCode=` on
// web, `/pools/join?code=` on the phone). ISSUE-1340.
const JOIN_LINK_PATH = /\/join\/([0-9a-z]+)/i;
const JOIN_LINK_PARAM = /[?&](?:joinCode|code)=([0-9a-z]+)/i;
const LOOKS_LIKE_LINK = /[a-z][a-z0-9+.-]*:\/\/|www\.|\.com\b/i;

/**
 * Pulls the invite code out of whatever the fan pasted. A bare code comes back
 * untouched. A share link, or the whole share message with a link inside it,
 * comes back as the code the link carries. A link with no code in it comes
 * back empty: stripping it to its letters would turn
 * `https://app.sportsmet.com/join/ABCD1234` into `HTTPSAPP`, a code nobody
 * sent (ISSUE-1340).
 */
export function extractJoinCodeFromText(raw: string): string {
  const pathMatch = JOIN_LINK_PATH.exec(raw);
  if (pathMatch?.[1] !== undefined) return pathMatch[1];
  const paramMatch = JOIN_LINK_PARAM.exec(raw);
  if (paramMatch?.[1] !== undefined) return paramMatch[1];
  if (LOOKS_LIKE_LINK.test(raw)) return "";
  return raw;
}

export function normalizeJoinCode(raw: string): string {
  const upper = extractJoinCodeFromText(raw).toUpperCase();
  let normalized = "";
  for (const char of upper) {
    if (JOIN_CODE_ALPHABET.includes(char)) normalized += char;
  }
  return normalized.slice(0, JOIN_CODE_LENGTH);
}

export function isJoinCodeComplete(code: string): boolean {
  return code.length === JOIN_CODE_LENGTH;
}

/**
 * The invite code on the clipboard, or null when there is none. Stricter than
 * `normalizeJoinCode`, which helps a fan who is typing: a clipboard holds
 * whatever was copied last, and "see you sunday" happens to strip down to
 * eight valid characters. So the clipboard counts only when it is a link that
 * carries a code, or a code on its own (spaces and hyphens allowed)
 * (ISSUE-1340, ISSUE-1402).
 */
export function joinCodeFromClipboard(raw: string): string | null {
  const candidate = extractJoinCodeFromText(raw.trim());
  const code = normalizeJoinCode(candidate);
  const compact = candidate.toUpperCase().replace(/[\s-]/g, "");
  return isJoinCodeComplete(code) && compact === code ? code : null;
}

export type typeJoinAvailability = { canJoin: boolean; notice: string | null };

export function resolveJoinAvailability(input: {
  isAlreadyMember: boolean;
  membershipStatus: string | null | undefined;
}): typeJoinAvailability {
  const status =
    input.membershipStatus ?? (input.isAlreadyMember ? "active" : null);

  switch (status) {
    case "active":
      return { canJoin: false, notice: "You're already in this pool." };
    case "pending":
      return {
        canJoin: false,
        notice: "You already asked to join. An admin has to say yes.",
      };
    case "suspended":
      return {
        canJoin: false,
        notice: "You're suspended in this pool. Ask an admin about it.",
      };
    case "removed":
      return {
        canJoin: false,
        notice:
          "An admin removed you from this pool. Ask them to add you back.",
      };
    case "rejected":
      return {
        canJoin: false,
        notice: "An admin turned down your last request. Ask them to add you.",
      };
    case "left":
    case null:
    default:
      return { canJoin: true, notice: null };
  }
}

// One request id per join INTENT, not per tap. The screen mints it once when
// the fan reaches the preview and reuses it for every tap of "Join pool", so
// a double-tap arrives at the server under one key and the shared
// idempotency helper replays instead of joining twice.
//
// Deliberately dependency-free: the key only has to be unique within one
// fan's account, which the server scopes it to anyway.
const requestIdBlock = (): string =>
  Math.random().toString(36).slice(2).padStart(11, "0").slice(0, 11);

export function newJoinRequestId(): string {
  return `${Date.now().toString(36)}-${requestIdBlock()}-${requestIdBlock()}`;
}
